/**
 * Ввод телефона Узбекистана.
 *
 * Номер один на всю систему: `+998` и девять цифр — так его хранит база, так его
 * проверяет контракт (`^\+998[0-9]{9}$`), и так его набирает человек. На экране цифры
 * разбиты на группы, потому что двенадцать подряд не читаются и ошибка в середине
 * остаётся незамеченной до отказа входа.
 *
 * Функции чистые и лежат отдельно от поля ввода: маска ломается не при наборе, а при
 * вставке из буфера — «+998 93 380-71-77», «998933807177», «93 380 71 77» — и проверять
 * это нужно тестом, а не глазами.
 */

const COUNTRY_CODE = '998';
const NATIONAL_LENGTH = 9;

/** Группы national-части: 93 380 71 77 — так номер произносят вслух. */
const GROUPS = [2, 3, 2, 2];

/**
 * Цифры номера без кода страны.
 *
 * Разбор зависит от того, написан ли код страны явно. Поле всегда показывает «+998»,
 * поэтому при наборе руками мы всегда в первой ветке. Вторая нужна для вставки из
 * буфера: у оператора 99 собственный номер начинается на те же цифры, что и код страны,
 * и безусловное отсечение «998» превратило бы 99 888 88 88 в 888 88 88.
 */
function nationalDigits(raw: string): string {
  const value = withoutFieldPrefix(raw);
  const digits = value.replace(/\D/g, '');
  const explicitCountry = value.startsWith('+');

  const national =
    explicitCountry || digits.length > NATIONAL_LENGTH ? stripCountryCode(digits) : digits;

  // Набор руками «998933807177» в поле, где уже стоит «+998»: маска разбирает каждую
  // цифру отдельно, и код страны оседает в номере. По одной цифре не отличить, набирается
  // ли код страны или лишняя цифра номера оператора 99, поэтому номер на «998» длиннее
  // девяти цифр не обрезается, а ждёт: дошёл до двенадцати — код страны снимается.
  if (national.length > NATIONAL_LENGTH && national.startsWith(COUNTRY_CODE)) {
    const typed = national.slice(0, NATIONAL_LENGTH + COUNTRY_CODE.length);

    return typed.length === NATIONAL_LENGTH + COUNTRY_CODE.length ? stripCountryCode(typed) : typed;
  }

  return national.slice(0, NATIONAL_LENGTH);
}

/**
 * Префикс поля, к которому приклеилась вставка.
 *
 * Поле показывает «+998» с самого начала, и номер, вставленный целиком, оказывается
 * за ним: «+998+998 93…» или «+998998933807177». Склейку выдаёт отсутствие пробела
 * после префикса — маска всегда ставит пробел перед первой группой, поэтому набор
 * руками («+998 99 888…») сюда не попадает.
 */
function withoutFieldPrefix(raw: string): string {
  const value = raw.trimStart();

  return FIELD_PREFIX_GLUED.test(value) ? value.slice(FIELD_PREFIX.length) : value;
}

const FIELD_PREFIX = `+${COUNTRY_CODE}`;
const FIELD_PREFIX_GLUED = new RegExp(`^\\+${COUNTRY_CODE}(?=\\+|${COUNTRY_CODE})`);

function stripCountryCode(digits: string): string {
  return digits.startsWith(COUNTRY_CODE) ? digits.slice(COUNTRY_CODE.length) : digits;
}

/** Как номер выглядит в поле: `+998 93 380 71 77`. */
export function formatPhoneInput(raw: string): string {
  const national = nationalDigits(raw);
  const parts: string[] = [];
  let offset = 0;

  for (const size of GROUPS) {
    if (offset >= national.length) {
      break;
    }

    parts.push(national.slice(offset, offset + size));
    offset += size;
  }

  // Хвост набираемого кода страны: показать, а не проглотить, иначе цифра исчезнет из поля.
  if (offset < national.length) {
    parts.push(national.slice(offset));
  }

  return parts.length === 0 ? `+${COUNTRY_CODE}` : `+${COUNTRY_CODE} ${parts.join(' ')}`;
}

/** Как номер уходит на сервер: `+998933807177`, без пробелов. */
export function phoneToE164(raw: string): string {
  return `+${COUNTRY_CODE}${nationalDigits(raw)}`;
}

/** Набран ли номер целиком. До этого кнопка входа ничего полезного не сделает. */
export function isPhoneComplete(raw: string): boolean {
  return nationalDigits(raw).length === NATIONAL_LENGTH;
}

/**
 * Номер, переданный соседней инсталляцией при переходе из шапки (Р-015).
 *
 * Приходит во фрагменте адреса (`#phone=%2B998…`): фрагмент не уходит на сервер и не
 * оседает в журналах прокси. Нет номера или он не похож на телефон — `null`.
 */
export function phoneFromHash(hash: string): string | null {
  const value = new URLSearchParams(hash.replace(/^#/, '')).get('phone');

  return value !== null && isPhoneComplete(value) ? formatPhoneInput(value) : null;
}
